import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import camelcaseKeys from "camelcase-keys";
import { produce } from "immer";
import snakecaseKeys from "snakecase-keys";

import { authorizedPatch } from "../../authorizedRequests";
import { Customer } from "../Customer/schema";
import { CustomerTab } from "../CustomerTab/schema";
import { useSession } from "../Session/SessionContext";
import { addCustomerSession } from "./reducer";
import {
  createCustomerSessionForSelf,
  createSession,
  fetchSession,
} from "./requests";
import { BillSplit, BillSplitSchema } from "./schema";

export const billSplitKeys = {
  current: (customerTabUuid: string) => [
    "billSplit",
    customerTabUuid?.toLowerCase(),
  ],
};

const billSplitQueryOptions = (customerTabUuid?: string) =>
  queryOptions({
    queryKey: billSplitKeys.current(customerTabUuid || "undefined"),
    queryFn: () =>
      fetchSession({ customerTabUuid: customerTabUuid || "undefined" }),
    enabled: !!customerTabUuid,
  });

export const useBillSplitQuery = (customerTabUuid?: string) => {
  return useQuery(billSplitQueryOptions(customerTabUuid));
};

export const useLoadedBillSplit = () => {
  const { data, isError, error, isFetching, isPending } = useCurrentBillSplit();
  if (isError) {
    throw `Error loading query, ${error.message}`;
  }
  if (isPending) {
    throw `Attempt to access loaded billSplit session that is  unloaded`;
  }
  if (!data) {
    throw `Attempt to access billSplit session that is  missing`;
  }

  return {
    data,
    isFetching,
  };
};

export const useCurrentBillSplit = () => {
  const { customerTab } = useSession();
  if (!customerTab?.uuid) {
    throw "Missing a customer tab";
  }
  return useBillSplit(customerTab.uuid);
};

export const useBillSplit = (customerTabUuid: string) =>
  useQuery({
    queryKey: billSplitKeys.current(customerTabUuid),
    queryFn: () => fetchSession({ customerTabUuid }),
  });

export const useCreateBillSplitCustomerSession = (customerTab: CustomerTab) => {
  const queryClient = useQueryClient();
  const { data: billSplitSession } = useBillSplit(customerTab.uuid);
  return useMutation({
    mutationFn: async () => {
      if (!billSplitSession) {
        throw "no bill split session";
      }
      const customerSession = await createCustomerSessionForSelf({
        billSplitUuid: billSplitSession.uuid,
      });
      queryClient.setQueryData(
        billSplitKeys.current(customerTab.uuid),
        produce(billSplitSession, (draft) => {
          addCustomerSession(draft, customerSession);
        }),
      );
    },
  });
};

export const useCreateBillSplit = (customerTab: CustomerTab) => {
  const queryClient = useQueryClient();
  const uuid = customerTab?.uuid;
  if (!uuid) {
    throw "no uuid";
  }
  return useMutation({
    mutationFn: async () => {
      const billSplitSession = await createSession({
        customerTabUuid: uuid,
      });
      queryClient.setQueryData(
        billSplitKeys.current(customerTab.uuid),
        billSplitSession,
      );
    },
  });
};
export const updateBillSplitSession = async (
  data: Partial<BillSplit> & { uuid: string },
): Promise<BillSplit> => {
  console.log({
    snak: snakecaseKeys(data),
    data,
  });
  const res = await authorizedPatch({
    url: `/v1/bill_splits/${data.uuid}`,
    body: {
      bill_split: snakecaseKeys(data),
    },
  });
  const billSplit = BillSplitSchema.parse(
    camelcaseKeys(res.body.bill_split, { deep: true }),
  );
  return billSplit;
};
export const useClaimRemainder = (billSplitSession: BillSplit) => {
  const queryClient = useQueryClient();
  const { customerTab } = useSession();
  return useMutation({
    mutationFn: async (customer: Customer | null) => {
      return await updateBillSplitSession({
        uuid: billSplitSession.uuid,
        remainderClaimerId: customer?.uuid || null,
      });
    },
    onSuccess: (data, variables, context) => {
      console.log("onSuccess", {
        customerTabId: customerTab?.uuid,
        key: billSplitKeys.current(billSplitSession.customerTabId),
      });
      queryClient.setQueryData(
        billSplitQueryOptions(billSplitSession.customerTabId).queryKey,
        data,
      );
    },
  });
};
